import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Stethoscope, Clock, MessageSquare, Info, ChevronRight } from 'lucide-react';

const ConsultingDoctors = ({ symptom }) => {
  const navigate = useNavigate();
  // Deduplicate by doctorId — keep the earliest startedAt per doctor
  const consultingDoctors = Object.values(
    (symptom?.consultingDoctors || []).reduce((acc, d) => {
      const id = d.doctorId;
      if (!acc[id] || new Date(d.startedAt) < new Date(acc[id].startedAt)) {
        acc[id] = d;
      }
      return acc;
    }, {})
  );

  const handleChatWithDoctor = (doctorId) => {
    const roomId = `symptom_${symptom._id}_doctor_${doctorId}`;
    navigate(`/chat/${symptom._id}?room=${roomId}`);
  };

  return (
    <div className="bg-white rounded-2xl shadow-premium hover:shadow-premium-hover transition-all duration-500 border border-outline-variant/10 overflow-hidden relative">
      {/* Decorative gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-secondary/[0.02] to-transparent pointer-events-none" />
      <div className="absolute -top-16 -right-16 w-40 h-40 bg-secondary/5 rounded-full blur-[80px] pointer-events-none" />

      <div className="p-7 space-y-6 relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-secondary/10">
              <Stethoscope className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Care Team</p>
              <h3 className="font-headline text-xl font-bold text-slate-900 tracking-tight">Doctors Reviewing</h3>
            </div>
          </div>
          <span className="flex items-center gap-2 bg-slate-50 border border-outline-variant/20 px-4 py-2 rounded-full text-xs font-bold text-slate-600">
            <span className="w-2 h-2 rounded-full bg-secondary" />
            {consultingDoctors.length} {consultingDoctors.length === 1 ? 'doctor' : 'doctors'}
          </span>
        </div>

        {consultingDoctors.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-outline-variant/30 rounded-2xl">
            <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
              <Clock className="w-7 h-7 text-slate-300" />
            </div>
            <p className="font-headline text-sm font-bold text-slate-800 mb-1">Waiting for review</p>
            <p className="text-xs text-slate-500">You'll be notified when a doctor starts reviewing your case.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {consultingDoctors.map((doctor, index) => {
              const name = doctor.doctorName || 'Unknown';
              const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
              return (
                <div
                  key={doctor.doctorId || index}
                  className="bg-slate-50 rounded-2xl p-5 border border-outline-variant/20 hover:bg-white hover:shadow-premium transition-all duration-500 group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center font-bold text-sm text-primary">
                        {initials}
                      </div>
                      <div>
                        <p className="font-headline font-bold text-sm text-slate-900 group-hover:text-primary transition-colors">Dr. {name}</p>
                        <p className="text-xs text-slate-500 font-semibold tracking-wider">
                          Reviewing since {new Date(doctor.startedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleChatWithDoctor(doctor.doctorId)}
                      className="px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-primary transition-all shadow-sm hover:shadow-primary/25 active:scale-[0.98]"
                    >
                      Chat
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info footer */}
      <div className="px-7 pb-7 relative z-10">
        <div className="p-5 bg-primary/5 rounded-2xl border border-primary/10 flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Info className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-xs font-extrabold text-primary uppercase tracking-[0.2em] mb-1">How it works</p>
            <p className="text-[13px] text-slate-500 leading-relaxed font-medium">
              Doctors collaborate privately to discuss your symptoms and ensure the best diagnosis. Each doctor can chat with you privately to ask questions and provide personalized advice.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultingDoctors;
